import { useEffect, useRef, type ReactNode } from 'react';

type MobileBottomSheetProps = {
  /** Contrôle l'ouverture — le composant ne monte rien quand `false`. */
  open: boolean;
  /** Appelé à la fermeture (backdrop, Échap, bouton Fermer). */
  onClose: () => void;
  /** Titre affiché dans l'entête, utilisé aussi pour `aria-labelledby`. */
  title: string;
  /** Libellé du bouton de fermeture (icône ✕ par défaut, jamais un emoji). */
  closeLabel?: string;
  children: ReactNode;
  className?: string;
};

let sheetIdCounter = 0;

/**
 * Feuille modale mobile générique (MOB-010/MOB-012).
 *
 * Ne connaît aucune règle métier : elle reçoit tout en props (titre, contenu)
 * et n'accède ni à Dexie ni à un store applicatif. Gère uniquement :
 * - le verrouillage du scroll de la page pendant l'ouverture,
 * - la sémantique d'accessibilité (`role="dialog"`, `aria-modal`),
 * - le déplacement du focus dans la feuille à l'ouverture et sa restauration
 *   à la fermeture,
 * - la fermeture au clic sur le fond et à la touche Échap.
 *
 * Reste minimal : composant unique, pas de sous-abstraction supplémentaire
 * tant qu'un second appelant ne le justifie pas.
 */
export function MobileBottomSheet({ open, onClose, title, closeLabel = 'Fermer', children, className = '' }: MobileBottomSheetProps) {
  const panelRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const titleIdRef = useRef<string>(`ix-mobile-sheet-title-${++sheetIdCounter}`);

  // Verrouille le scroll de la page tant que la feuille est ouverte — évite
  // le double scroll (page + feuille) sur mobile (MOB-013).
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Focus déplacé dans la feuille à l'ouverture, restauré au déclencheur à
  // la fermeture (MOB-012/MOB-014).
  useEffect(() => {
    if (open) {
      lastFocusedRef.current = document.activeElement as HTMLElement | null;
      const id = requestAnimationFrame(() => closeButtonRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    lastFocusedRef.current?.focus();
    return undefined;
  }, [open]);

  // Échap ferme la feuille (MOB-012). Tab/Shift+Tab piège le focus à
  // l'intérieur du panneau (MOB-012) : en bout de liste, il boucle sur le
  // premier/dernier élément focusable plutôt que de sortir vers la page
  // sous-jacente.
  useEffect(() => {
    if (!open) return undefined;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="ix-mobile-sheet-backdrop" onClick={onClose} data-testid="mobile-sheet-backdrop" />
      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleIdRef.current}
        className={`ix-mobile-sheet ${className}`.trim()}
      >
        <div className="ix-mobile-sheet-head">
          <p id={titleIdRef.current} className="ix-mobile-sheet-title">{title}</p>
          <button
            ref={closeButtonRef}
            type="button"
            className="ix-mobile-sheet-close"
            onClick={onClose}
            aria-label={closeLabel}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="ix-mobile-sheet-body">{children}</div>
      </section>
    </>
  );
}
